// ─────────────────────────────────────────────────────────────
// CardBadges — compact visual indicators for status, priority,
// value, and tags.  Used on every card surface.
// ─────────────────────────────────────────────────────────────

import { Box, Chip, Tooltip, Typography } from "@mui/material";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import type { SxProps, Theme } from "@mui/material";
import type { CardStatus, PriorityLevel, ValueLevel } from "../types";
import { STATUS_COLORS, PRIORITY_COLORS } from "../theme";

interface StatusBadgeProps {
  status: CardStatus;
  sx?: SxProps<Theme>;
}

export function StatusBadge({ status, sx }: StatusBadgeProps) {
  return (
    <Chip
      label={status}
      size="small"
      sx={[
        {
          backgroundColor: STATUS_COLORS[status] ?? "#607d8b",
          color: "#fff",
          fontWeight: 500,
          fontSize: "0.65rem",
          height: "18px",
          borderRadius: "3px",
          opacity: 0.9,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}

interface PriorityBadgeProps {
  priority: PriorityLevel;
  sx?: SxProps<Theme>;
}

// Colored chip for priority — shown in card detail and forms
export function PriorityBadge({ priority, sx }: PriorityBadgeProps) {
  const color = PRIORITY_COLORS[priority] ?? "#607d8b";
  return (
    <Chip
      label={priority}
      size="small"
      sx={[
        {
          backgroundColor: color,
          color: "#fff",
          fontWeight: 600,
          fontSize: "0.65rem",
          height: "18px",
          borderRadius: "3px",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );
}

interface ValueLabelProps {
  value: ValueLevel | undefined;
  sx?: SxProps<Theme>;
}

// Plain text value label — no color coding needed
export function ValueLabel({ value, sx }: ValueLabelProps) {
  if (!value) return null;
  return (
    <Box
      component="span"
      sx={[
        {
          fontSize: "0.65rem",
          color: "text.secondary",
          fontStyle: "italic",
          whiteSpace: "nowrap",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {value}
    </Box>
  );
}

interface TagListProps {
  tags: string[];
  max?: number;
  sx?: SxProps<Theme>;
}

export function TagList({ tags, max = 3, sx }: TagListProps) {
  const visible = tags.slice(0, max);
  const overflow = tags.length - visible.length;

  return (
    <Box sx={[{ display: "flex", flexWrap: "wrap", gap: 0.5 }, ...(Array.isArray(sx) ? sx : [sx])]}>
      {visible.map((tag) => (
        <Chip
          key={tag}
          label={tag}
          size="small"
          variant="outlined"
          sx={{
            fontSize: "0.65rem",
            height: "18px",
            borderRadius: "3px",
            opacity: 0.8,
          }}
        />
      ))}
      {overflow > 0 && (
        <Tooltip title={tags.slice(max).join(", ")}>
          <Chip
            label={`+${overflow}`}
            size="small"
            sx={{
              fontSize: "0.65rem",
              height: "18px",
              borderRadius: "3px",
              opacity: 0.6,
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
}

interface PriorityDotProps {
  priority: PriorityLevel;
}

// Small colored dot — shown on dense card rows / kanban cards
export function PriorityDot({ priority }: PriorityDotProps) {
  const color = PRIORITY_COLORS[priority] ?? "#607d8b";
  return (
    <Tooltip title={`${priority} priority`}>
      <Box
        component="span"
        sx={{
          display: "inline-block",
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: color,
          flexShrink: 0,
        }}
      />
    </Tooltip>
  );
}

interface SubmittedByProps {
  name: string | null | undefined;
  sx?: SxProps<Theme>;
}

// Subtle submitter attribution — person icon + name in secondary caption text
export function SubmittedBy({ name, sx }: SubmittedByProps) {
  if (!name) return null;
  return (
    <Box
      sx={[
        {
          display: "flex",
          alignItems: "center",
          gap: "2px",
          color: "text.secondary",
          flexShrink: 0,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <PersonOutlinedIcon sx={{ fontSize: "0.75rem" }} />
      <Typography variant="caption" color="text.secondary" noWrap>
        {name}
      </Typography>
    </Box>
  );
}
